import { useMemo, useState } from "react";
import { schuettgut, MATERIALIEN, MATERIAL_KEYS } from "@/lib/schuettgut";
import { trackSettled, track } from "@/lib/track";

const de = (v) => (Number(v) || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

function NumberField({ label, value, onChange, suffix }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center rounded-lg border bg-background focus-within:ring-2 focus-within:ring-primary/40">
        <input type="number" inputMode="decimal" min={0} value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-full bg-transparent px-3 py-2 outline-none" />
        <span className="px-3 text-sm text-muted-foreground">{suffix}</span>
      </div>
    </label>
  );
}

export default function SchuettgutRechner({ material: initialMaterial = "schotter" }) {
  const [material, setMaterial] = useState(MATERIAL_KEYS.includes(initialMaterial) ? initialMaterial : "schotter");
  const [inputs, setInputs] = useState({ laenge: 10, breite: 5, hoehe: 5 });

  const res = useMemo(() => schuettgut(material, inputs), [material, inputs]);

  const set = (patch) => {
    setInputs((p) => ({ ...p, ...patch }));
    trackSettled("schuettgut_berechnet", { calculator: material });
  };
  const switchMaterial = (m) => { setMaterial(m); track("schuettgut_material", { calculator: m }); };

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-6 shadow-sm">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Material</span>
            <select
              value={material}
              onChange={(e) => switchMaterial(e.target.value)}
              data-testid="material-select"
              className="rounded-lg border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40"
            >
              {MATERIAL_KEYS.map((k) => (
                <option key={k} value={k}>{MATERIALIEN[k].label} ({de(MATERIALIEN[k].dichte)} t/m³)</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <NumberField label="Länge" value={inputs.laenge} onChange={(v) => set({ laenge: v })} suffix="m" />
            <NumberField label="Breite" value={inputs.breite} onChange={(v) => set({ breite: v })} suffix="m" />
          </div>
          <NumberField label="Schütthöhe" value={inputs.hoehe} onChange={(v) => set({ hoehe: v })} suffix="cm" />
        </div>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl bg-secondary/60 p-4">
            <div className="text-sm text-muted-foreground">Benötigte Menge ({de(res.flaeche)} m²)</div>
            <div className="text-2xl font-semibold mt-0.5" data-testid="schuettgut-ergebnis">{de(res.volumen)} m³</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg bg-background/70 p-2">
                <div className="text-lg font-semibold">{de(res.gewichtTonnen)} t</div>
                <div className="text-xs text-muted-foreground">Gewicht ({de(res.gewichtKg)} kg)</div>
              </div>
              <div className="rounded-lg bg-background/70 p-2">
                <div className="text-lg font-semibold">{res.bigbags}</div>
                <div className="text-xs text-muted-foreground">Big Bags à 1 t</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Schätzung mit loser Schüttdichte ({de(res.dichte)} t/m³). Verdichtet liegt das Gewicht etwas höher. Big Bags à 1 Tonne, da das Gewicht und nicht das Volumen die Füllmenge begrenzt.
          </p>
        </div>
      </div>
    </div>
  );
}
